import React from 'react';
import { useTable, Column, Row as RTRow } from 'react-table';

import {
  Table, TableBody, TableRow, TableCell, Box,
  TableHead
} from '@mui/material';


import { TableProps } from '../Table';
import { NoItemsOverlayBody } from '../Table/Overlay';
import { Loader } from '../Loader';
import { MobileListRow } from './MobileListRow.component';
import { useElementInView } from '@uplift/hooks';
import { Modal, ModalContent, ModalTitle } from '../Modal';


export type MobileListProps<Row extends Record<string, unknown>> = TableProps<Row> & {
  disableCollapsibleRowFixedTableLayout?: boolean;
  rowDetailsComponent?: React.ComponentType<{ row: Row }>;
};


export function MobileList<Row extends Record<string, unknown>>({
  rows: data,
  columns,
  loading,
  onInfiniteScroll,
  rowDetailsComponent: RowDetailsComponent,
  ...props
}: MobileListProps<Row>) {
  const [ selected, setSelected ] = React.useState<Row>();
  const hasInfiniteScroll = !!onInfiniteScroll;
  const _columns: readonly Column<Row>[] = React.useMemo(() => {
    return columns.map<Column<Row>>((c, index) => {
      const res: Column<Row> = {
        id: index.toString(),
        accessor: c.field,
        Header: c.headerName,
        minWidth: c.minWidth,
        width: c.width,
      };

      if (c?.renderCell) {
        res.Cell = (props) => {
          return c.renderCell?.({ row: props.cell.row.original, value: props.cell.value });
        };
      }

      return res;
    });
  }, [ columns ]);

  const {
    rows,
    prepareRow,
    ...table
  } = useTable({
    columns: _columns,
    data: data,
  });

  const tableRef = React.useRef<HTMLTableElement | null>(null);
  const loadedRef = React.useRef<Record<string, boolean>>({});

  const {
    elementRef,
    inView
  } = useElementInView({ options: {}, disabled: !hasInfiniteScroll, initialViewState: false });


  React.useEffect(() => {
    if (inView) {
      const currentKey = data.map(d => d.id).join(',');

      if (loadedRef.current[currentKey]) return;
      loadedRef.current[currentKey] = true;
      onInfiniteScroll?.();
    }
  }, [ inView ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => () => {
    loadedRef.current = {};
  }, []);

  if (!rows.length) {
    return <NoItemsOverlayBody icon={props.emptyIcon} message={props.emptyMessage} />;
  }

  return (
    <>
      <Table
        {...table.getTableProps()}
        size="small"
        style={{ width: '100%' }}
        ref={tableRef}
      >
        <TableHead>
          <TableRow>
            <TableCell colSpan={_columns.length + 1} />
          </TableRow>
        </TableHead>
        <TableBody {...table.getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            const { key, ...rowProps } = row.getRowProps();

            return (
              <MobileListRow
                key={key}
                row={row as RTRow<Record<string, unknown>>}
                onClick={() => setSelected(data.find(d => d.id === row.original.id))}
                {...rowProps}
              />
            );
          })}
          {hasInfiniteScroll && (
            <TableRow ref={elementRef}>
              <TableCell style={{ borderBottom: 0 }} colSpan={columns.length}>
                {loading ? (
                  <Loader width="100%" color="primary" size="24px" thickness={5} mt={2} />
                ) : (
                  <Box height="24px" mt={2} />
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {RowDetailsComponent && (
        <Modal open={!!selected} onClose={() => setSelected(undefined)} fullScreen>
          <ModalTitle onClose={() => setSelected(undefined)}>Details</ModalTitle>
          <ModalContent noPadding dividers>
            {selected && <RowDetailsComponent row={selected} />}
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
